import { Controller, useForm } from "react-hook-form";
import Radio from "~/common/components/Radio";
import CustomNumberFormat from "~/common/components/CustomNumberFormat";
import { CloseBtn } from "~/features/addToCart/components/AtcCloseButton";
import { Button } from "~/common/mui";
import { useEffect, useState } from "react";
import { getHeaterSelection } from "~/features/selectionAssistant/heater/api/getHeaterSelection";
import { type ResultsHeaterProps } from "~/pages/HeaterSelectionAssistantPage/HeaterSelectionAssistantPage";
import PreviewTableMaterialIdInput from "~/features/bulkAddToCart/components/PreviewTable/PreviewTableMaterialIdInput";
import { type ProductData } from "~/domains/product/types/ProductData";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { trackHeaterSelection } from "~/app/analytics/ga/ga";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/mui/Alert/Alert";
import {
  Form as FormWrapper,
  SelectLabel,
  FormRow,
  FormColumn,
  FormRadioMain,
  SectionImageInputs,
  DivImageInputs,
  FormItemImage,
  SectionMobile,
  Section,
  FormButtonSection,
  FormItem,
  SpanDisabled,
  SpanEnabled,
  RadioSwitchOption,
  RadioSwitchOptions,
} from "~/pages/HeaterSelectionAssistantPage/HeaterSelectionAssistantPage.styles";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

interface FormProps {
  handleChange: (values: ResultsHeaterProps[] | null) => void;
}

interface HeaterSelectionAssistantForm {
  designation: ProductData | null;
  isMM: boolean;
  hsa_id: number | null;
  hsa_od: number | null;
  hsa_w: number | null;
  isKg: boolean;
  hsa_m: number | null;
}

export function FormHS({ handleChange }: FormProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isDesignation, setIsDesignation] = useState(false);
  const isMinDesktop = useIsMinDesktopSize();
  const isMinTablet = useIsMinTabletSize();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<HeaterSelectionAssistantForm>({
    defaultValues: {
      designation: null,
      isMM: true,
      hsa_id: null,
      hsa_od: null,
      hsa_w: null,
      isKg: true,
      hsa_m: null,
    },
    mode: "onChange",
  });

  const designation = watch("designation");
  const innerDiameter = watch("hsa_id");
  const outsideDiameter = watch("hsa_od");
  const width = watch("hsa_w");
  const mass = watch("hsa_m");

  const errorDiameter = innerDiameter && outsideDiameter ? innerDiameter >= outsideDiameter : false;

  const isApplyDisabled = isDesignation
    ? !designation
    : !(!!innerDiameter && !!outsideDiameter && !!width && !!mass) || errorDiameter;

  const resetForm = () => {
    reset();
    handleChange(null);
    if (isDesignation) {
      setIsDesignation(false);
    }
  };

  useEffect(() => {
    if (!isDesignation && errors.designation) {
      clearErrors("designation");
    }
  }, [clearErrors, errors.designation, isDesignation]);

  const onSubmitHandler = handleSubmit(async (values) => {
    handleChange(null);
    setIsLoading(true);
    if (!isApplyDisabled) {
      const unit = values.isMM ? "mm" : "inch";
      const unitMass = values.isKg ? "kg" : "lbs";
      const props = {
        code: isDesignation ? values?.designation?.code ?? "" : "",
        attributes: !isDesignation
          ? [
              {
                code: "hsa_id",
                unit: unit,
                value: String(values.hsa_id ?? ""),
              },
              {
                code: "hsa_od",
                unit: unit,
                value: String(values.hsa_od ?? ""),
              },
              {
                code: "hsa_w",
                unit: unit,
                value: String(values.hsa_w ?? ""),
              },
              {
                code: "hsa_m",
                unit: unitMass,
                value: String(values.hsa_m ?? ""),
              },
            ]
          : [],
      };
      const attributes = {
        length_unit: unit,
        mass_unit: unitMass,
        inside_diameter: values.hsa_id,
        outside_diameter: values.hsa_od,
        width: values.hsa_w,
        mass: values.hsa_m,
      };
      const result: ResultsHeaterProps[] | null = await getHeaterSelection(props);
      if (result) {
        handleChange(result);
        trackHeaterSelection(isDesignation, values?.designation?.designation ?? "", attributes);
      }
    }
    setIsLoading(false);
  });

  const controllerWeightRadio = () => {
    return (
      <Controller
        control={control}
        name={"isKg"}
        render={({ field: { value, onChange } }) => (
          <RadioSwitchOptions className="scha__radio-switch__options">
            <RadioSwitchOption className="scha__radio-switch__option" onClick={() => !isDesignation && onChange(true)}>
              <Radio active={value === true} className={"icon-margin"} disabled={isDesignation} />
              {isDesignation ? (
                <SpanDisabled>{t("heaterSelectionAssistant.form.radio.kg")}</SpanDisabled>
              ) : (
                <SpanEnabled>{t("heaterSelectionAssistant.form.radio.kg")}</SpanEnabled>
              )}
            </RadioSwitchOption>
            <RadioSwitchOption className="scha__radio-switch__option" onClick={() => !isDesignation && onChange(false)}>
              <Radio active={value === false} className={"icon-margin"} disabled={isDesignation} />
              {isDesignation ? (
                <SpanDisabled>{t("heaterSelectionAssistant.form.radio.lbs")}</SpanDisabled>
              ) : (
                <SpanEnabled>{t("heaterSelectionAssistant.form.radio.lbs")}</SpanEnabled>
              )}
            </RadioSwitchOption>
          </RadioSwitchOptions>
        )}
      />
    );
  };

  return (
    <FormWrapper>
      <SelectLabel>{t("heaterSelectionAssistant.form.selectLabel")}</SelectLabel>
      <FormRow>
        <FormColumn>
          <FormRadioMain>
            <RadioSwitchOption
              className={"scha__radio-switch__option"}
              onClick={() => setIsDesignation(true)}
              tracking-aux-bearing-switch="designation"
            >
              <Radio active={isDesignation} className={"icon-margin"} />
              <span>{t("heaterSelectionAssistant.form.radio.designation")}</span>
            </RadioSwitchOption>
          </FormRadioMain>
          <Controller
            control={control}
            name={"designation"}
            rules={{
              required: isDesignation,
              validate: isDesignation
                ? (value) => !!value?.designation || `${t("heaterSelectionAssistant.form.input.errorProduct")}`
                : undefined,
            }}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
              <div>
                <PreviewTableMaterialIdInput
                  onChange={(data) => {
                    if (!data?.designation) {
                      onChange({ code: undefined });
                    }
                    onChange(data);
                  }}
                  onBlur={onBlur}
                  value={value?.designation ? value.designation : ""}
                  label={t("heaterSelectionAssistant.form.input.designation")}
                  data-test-id={""}
                  helperText={error?.message}
                  error={!!error?.message}
                  disabled={!isDesignation}
                />
              </div>
            )}
          />
        </FormColumn>
        <FormColumn>
          <FormRadioMain>
            <RadioSwitchOption
              className={"scha__radio-switch__option"}
              onClick={() => setIsDesignation(false)}
              tracking-aux-bearing-switch="attributes"
            >
              <Radio active={!isDesignation} className={"icon-margin"} />
              <span>{t("heaterSelectionAssistant.form.radio.attributes")}</span>
            </RadioSwitchOption>
          </FormRadioMain>

          <SectionImageInputs>
            <DivImageInputs>
              <FormItemImage
                style={{ opacity: isDesignation ? 0.5 : 1 }}
                src="https://cdn.schaeffler-ecommerce.com/cdn/001A3966_s04_3c_0k_0k.png"
                alt=""
              />

              {!isMinDesktop && isMinTablet && (
                <SectionMobile>
                  {controllerWeightRadio()}
                  <FormRow>
                    <Controller
                      control={control}
                      name={"hsa_m"}
                      rules={{
                        required: !isDesignation,
                        validate: !isDesignation
                          ? (value) =>
                              (value && value > 0) || `${t("heaterSelectionAssistant.form.input.error.greaterThan")}`
                          : undefined,
                      }}
                      render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <FormItem>
                          <CustomNumberFormat
                            label={t("heaterSelectionAssistant.form.input.mass.label")}
                            value={value}
                            handleState={(e) => onChange(e.floatValue)}
                            error={!!error && !isDesignation}
                            helperText={error?.message}
                            disabled={isDesignation}
                          />
                        </FormItem>
                      )}
                    />
                  </FormRow>
                </SectionMobile>
              )}
            </DivImageInputs>

            <Section>
              <Controller
                control={control}
                name={"isMM"}
                render={({ field: { value, onChange } }) => (
                  <RadioSwitchOptions className="scha__radio-switch__options">
                    <RadioSwitchOption
                      className="scha__radio-switch__option"
                      onClick={() => !isDesignation && onChange(true)}
                    >
                      <Radio active={value === true} className={"icon-margin"} disabled={isDesignation} />
                      {isDesignation ? (
                        <SpanDisabled>{t("heaterSelectionAssistant.form.radio.length.mm")}</SpanDisabled>
                      ) : (
                        <SpanEnabled>{t("heaterSelectionAssistant.form.radio.length.mm")}</SpanEnabled>
                      )}
                    </RadioSwitchOption>
                    <RadioSwitchOption
                      className="scha__radio-switch__option"
                      onClick={() => !isDesignation && onChange(false)}
                    >
                      <Radio active={value === false} className={"icon-margin"} disabled={isDesignation} />
                      {isDesignation ? (
                        <SpanDisabled>{t("heaterSelectionAssistant.form.radio.length.inch")}</SpanDisabled>
                      ) : (
                        <SpanEnabled>{t("heaterSelectionAssistant.form.radio.length.inch")}</SpanEnabled>
                      )}
                    </RadioSwitchOption>
                  </RadioSwitchOptions>
                )}
              />

              <FormRow>
                <Controller
                  control={control}
                  name={"hsa_id"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) =>
                          (value && value > 0) || `${t("heaterSelectionAssistant.form.input.error.greaterThan")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <FormItem>
                      <CustomNumberFormat
                        label={t("heaterSelectionAssistant.form.input.insideDiameter.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue?.toString())}
                        error={!!error && !isDesignation}
                        helperText={error?.message}
                        disabled={isDesignation}
                      />
                    </FormItem>
                  )}
                />

                <Controller
                  control={control}
                  name={"hsa_od"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) => (value && value > 0) || `${t("bearingFrequencyCalculator.error.numeric")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <FormItem>
                      <CustomNumberFormat
                        label={t("heaterSelectionAssistant.form.input.outsideDiameter.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue)}
                        error={(!!error || errorDiameter) && !isDesignation}
                        helperText={
                          !errorDiameter
                            ? error?.message
                            : t("heaterSelectionAssistant.form.input.error.outsideDiameter")
                        }
                        disabled={isDesignation}
                      />
                    </FormItem>
                  )}
                />

                <Controller
                  control={control}
                  name={"hsa_w"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) => (value && value > 0) || `${t("bearingFrequencyCalculator.error.numeric")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <FormItem>
                      <CustomNumberFormat
                        label={t("heaterSelectionAssistant.form.input.width.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue)}
                        error={!!error && !isDesignation}
                        helperText={error?.message}
                        disabled={isDesignation}
                      />
                    </FormItem>
                  )}
                />
              </FormRow>
            </Section>
          </SectionImageInputs>

          {(isMinDesktop || !isMinTablet) && (
            <Section>
              {controllerWeightRadio()}

              <FormRow>
                <Controller
                  control={control}
                  name={"hsa_m"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) =>
                          (value && value > 0) || `${t("heaterSelectionAssistant.form.input.error.greaterThan")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <FormItem>
                      <CustomNumberFormat
                        label={t("heaterSelectionAssistant.form.input.mass.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue)}
                        error={!!error && !isDesignation}
                        helperText={error?.message}
                        disabled={isDesignation}
                      />
                    </FormItem>
                  )}
                />
              </FormRow>
            </Section>
          )}
        </FormColumn>
      </FormRow>

      <FormButtonSection>
        <CloseBtn data-test-id="" onClick={() => resetForm()}>
          <RestartAltIcon className="icon-margin" />
          {t("heaterSelectionAssistant.form.resetAll")}
        </CloseBtn>

        <Button data-test-id="" onClick={() => onSubmitHandler()} disabled loading={isLoading}>
          {t("heaterSelectionAssistant.form.apply")}
        </Button>
      </FormButtonSection>

      <Alert severity="info">{t("heaterSelectionAssistant.form.blueMessage")}</Alert>
    </FormWrapper>
  );
}
